import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import Form, {
  SimpleItem,
  RequiredRule,
  GroupItem,
  ButtonItem
} from 'devextreme-react/form';
import { formatMessage } from 'devextreme/localization';
import { Column } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react';
import { NumberBox } from 'devextreme-react/number-box';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import styles from './wastebalancing.module.scss';
import columnDataGrid from './columnDataGrid';
import DataGridMVP from '../DataGridMVP';
import PopupMVP from '../PopupMVP';
import useWasteColumns from '../../hooks/useWasteColumns';

const cursor = {
  dateFrom: '',
  dateTo: ''
};

const WasteBalancing = ({
  plantId,
  wasteReducer: { wasteRolls, loading, averageWaste, loadingData },
  getWasteRolls,
  getReportCreate,
  reportReducer
}) => {
  /** Данные по таблице */
  const [thisDataGridInstance, setThisDataGridInstance] = useState({});
  const form = useRef(null);
  const [filterWasteDataGrid, setFilterWasteDataGrid] = useState({
    dateFrom: '',
    dateTo: ''
  });
  const [visiblePopup, setVisiblePopup] = useState(false);
  const [isExcel, setIsExcel] = useState(false);
  const [dross, setDross] = useState(null);
  const [wasteFormatData, quantityColumns] = useWasteColumns(wasteRolls);

  useEffect(() => {
    if (thisDataGridInstance.instance) {
      if (reportReducer.loading !== undefined && reportReducer.loading) {
        thisDataGridInstance.instance.beginCustomLoading('Loading...');
      } else {
        thisDataGridInstance.instance.endCustomLoading();
      }
    }
  }, [loading, thisDataGridInstance, reportReducer.loading]);

  const formOnSubmit = useCallback(
    (e) => {
      e.preventDefault();
      getWasteRolls({
        dateFrom: cursor.dateFrom,
        dateTo: cursor.dateTo,
        plantId
      });
    },
    [getWasteRolls, plantId]
  );

  const onValueChangeDross = useCallback(({ value }) => {
    setDross(value);
  }, []);
  const modalOnHiding = useCallback(() => {
    setVisiblePopup(false);
    setDross(null);
  }, []);

  const onOpenConfirm = useCallback(() => {
    setVisiblePopup(true);
  }, []);

  const onReportCreate = useCallback((val) => {
    if (val.printFormat === 'EXCEL') {
      setIsExcel(true);
    } else {
      setIsExcel(false);
    }
    getReportCreate({
      url: '/report',
      dateFrom: cursor.dateFrom,
      dateTo: cursor.dateTo,
      dross,
      reportType: 'PROCESSING_REPORT',
      printFormat: val.printFormat,
      plantId
    });
    setVisiblePopup(false);
    setDross(null);
  }, [
    getReportCreate,
    plantId,
    dross
  ]);

  const HeaderForm = useMemo(
    () => (
      <form onSubmit={formOnSubmit}>
        <Form
          className={styles.form}
          labelLocation="top"
          width="100%"
          readOnly={false}
          showColonAfterLabel
          showValidationSummary={false}
          formData={cursor}
          ref={form}
        >
          <GroupItem colCount={5}>
            <SimpleItem
              dataField="dateFrom"
              colSpan={2}
              editorType="dxDateBox"
              label={{ text: formatMessage('filters').dateFrom }}
              editorOptions={{
                stylingMode: 'filled',
                dateSerializationFormat: 'yyyy-MM-dd'
              }}
            >
              <RequiredRule message={formatMessage('textSelectItem')} />
            </SimpleItem>
            <SimpleItem
              colSpan={2}
              dataField="dateTo"
              editorType="dxDateBox"
              label={{ text: formatMessage('filters').dateEnd }}
              editorOptions={{
                stylingMode: 'filled',
                dateSerializationFormat: 'yyyy-MM-dd'
              }}
            >
              <RequiredRule message={formatMessage('textSelectItem')} />
            </SimpleItem>
            <ButtonItem
              colSpan={1}
              verticalAlignment="bottom"
              horizontalAlignment="center"
              buttonOptions={{
                text: formatMessage('textButton').create,
                type: 'default',
                useSubmitBehavior: true
              }}
            />
          </GroupItem>
        </Form>
      </form>
    ),
    [formOnSubmit]
  );

  // Обновление данных
  const onClickUpdate = useCallback(() => {
    if (filterWasteDataGrid) {
      getWasteRolls({
        ...filterWasteDataGrid,
        plantId
      });
    }
  }, [filterWasteDataGrid, getWasteRolls, plantId]);

  return (
    <>
      <div className={styles.header}>
        {HeaderForm}
        <Button
            text={formatMessage('textButton').confirm}
            type={'success'}
            stylingMode={'contained'}
            onClick={onOpenConfirm}
            disabled={!wasteRolls.length}
            className={styles.confirmButton}
          />
      </div>
      <div className={styles.wasteWrapper}>
        <DataGridMVP
        dataSource={wasteFormatData}
        setDataGridInstance={setThisDataGridInstance}
        filterDataGrid={filterWasteDataGrid}
        setFilterDataGrid={setFilterWasteDataGrid}
        className={styles.wasteTable}
        setStateStoringName="dataGridWasteBalancing"
        dataGridOnOptionChanged={() => {}}
        tText={formatMessage('textPeriod')}
        additionalFooterData={wasteRolls.length && `.  ${formatMessage('wasteBalalancingTotalAverage')}: ${averageWaste}%`}
        loadingData={loading}
        isDataLoading={loadingData}
        onClickUpdate={onClickUpdate}
        visibleButtonQRCode={false}
        visibleInfoIcon={false}
        selectionMode={false}
        visibleActyalButton={false}
        visibleSummary
        visibleButtonCheckAll
        visibleButtonUpdate
      >
          {columnDataGrid.map((item) => (
            <Column
            dataField={item.dataField}
            caption={item.caption}
            visible={!item.notVisible}
            allowSearch={false}
            allowFiltering
            key={item.dataField}
            dataType={item.dataType}
            cssClass={item.cssClass}
            width={item.width ? item.width : undefined}
          />
          ))}
          {quantityColumns}
        </DataGridMVP>
      </div>
      <PopupMVP visible={visiblePopup}
                onHiding={modalOnHiding}
                title={formatMessage('textReport')[13]}
                className={styles.modalDop}
      >
        <div className="formData">
          <div className="optionsData">
            <span>{formatMessage('textReportDross')}</span>
            <NumberBox value={dross}
                       stylingMode="filled"
                       placeholder={formatMessage('textReportDross')}
                       width={300}
                       onValueChanged={onValueChangeDross}
                       showClearButton
            />
          </div>
          <div className="formButton">
            <Button type="success"
                    stylingMode="contained"
                    width={150}
                    onClick={() => onReportCreate({
                      ...dross,
                      printFormat: 'EXCEL'
                    })}
            >
              <LoadIndicator className="indicator" visible={loading && isExcel} />
              <span>EXCEL</span>
            </Button>
            <Button text={formatMessage('textButton').cancel}
                    type="default"
                    stylingMode="text"
                    onClick={modalOnHiding}
            />
            <Button type="default"
                    stylingMode="contained"
                    width={200}
                    onClick={() => onReportCreate({
                      ...dross,
                      printFormat: 'PDF'
                    })}
            >
              <LoadIndicator className="indicator" visible={loading && !isExcel} />
              <span>{formatMessage('textButton').create}</span>
            </Button>
          </div>
        </div>
      </PopupMVP>
    </>
  );
};
export default WasteBalancing;
